import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import { handleApiError } from '../../api/errorMapping';
import { useAuth } from '../../hooks/useAuth';
import type { LabelResolver } from '../../hooks/useLabelResolver';
import { Gate } from '../permissions/Gate';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { StatusBadge } from './StatusBadge';
import { Button } from '../ui/button';
import { HelpHint } from '@/components/ui/HelpHint';
import { HELP_TEXT } from '@/lib/helpText';
import { ManageUserRolesDialog } from '../tenant/ManageUserRolesDialog';
import { assignmentRoleName, assignmentScopeLabel } from '../tenant/assignmentLabels';
import { EditUserDialog } from './EditUserDialog';
import { useTenantUserFields } from '../../hooks/useTenantUserFields';
import { CopyableId } from '@fiado/react-kit';
import { ApiUserCredentialsModal } from './ApiUserCredentialsModal';
import { EditApiAccessDialog } from './EditApiAccessDialog';
import { EditApiUserDialog } from './EditApiUserDialog';
import { API_USER_MANAGE_PERMISSION } from '../../lib/apiUserRoles';
import type {
    ApiUserCredentialsLocal,
    TenantUserListItemLocal,
    TenantUserDetailLocal,
    UpdateApiUserAccessRequestLocal,
    UpdateTenantUserRequestLocal,
} from '../../api/types/TenantLocal';
import type { Permission, Scope } from '../../api/types/permissions';

/**
 * Funciones de API que difieren por contexto (tenant / platform drill-down). Cada flujo inyecta su
 * binding (`tenantApi.*` / `platformApi.*TenantUser`) ya cerrado sobre los ids correctos, de modo que
 * el menú no sabe nada de tenantId concretos.
 */
export interface UserActionsApi {
    getDetail: () => Promise<TenantUserDetailLocal>;
    update: (body: UpdateTenantUserRequestLocal) => Promise<unknown>;
    disable: () => Promise<unknown>;
    enable: () => Promise<unknown>;
    delete: () => Promise<unknown>;
    resetPassword: () => Promise<{ temporaryPassword: string }>;
    resetMfa: () => Promise<unknown>;
    resendInvitation: () => Promise<unknown>;
    rotateApiSecret: () => Promise<ApiUserCredentialsLocal>;
    updateApiAccess: (body: UpdateApiUserAccessRequestLocal) => Promise<unknown>;
}

/**
 * Permisos del flujo. El prefijo del recurso `user.*` es `tenant.user.*`; la asignación de rol SIEMPRE
 * es `tenant.role.assign` (el backend re-enforza el scope).
 */
export interface UserActionsPerms {
    update: Permission;
    passwordReset: Permission;
    mfaReset: Permission;
    status: Permission;
    delete: Permission;
}

interface Props {
    user: TenantUserListItemLocal;
    /** Tenant donde viven las asignaciones de rol (drill-down platform / actor en retailer / tenant propio). */
    tenantId: string;
    /** Scope/scopeRef con que se gatean los items en la UI. El backend re-enforza. */
    gateScope: Scope;
    gateScopeRef: string;
    api: UserActionsApi;
    perms: UserActionsPerms;
    /** queryKey del DETALLE del usuario (para el dialog de roles y el de detalle). */
    detailQueryKey: QueryKey;
    /** queryKey del LISTADO de usuarios (para invalidar tras una acción). */
    listQueryKey: QueryKey;
    /** Resolvedor del tenant de las asignaciones (el visto en platform, el propio en tenant). */
    labels: LabelResolver;
}

/**
 * Menú "⋮" de acciones por fila de usuario, COMPARTIDO por los tres flujos de usuarios de tenant
 * (tenant propio, retailer, drill-down platform). Garantiza un set idéntico de acciones, en el mismo
 * orden y con los mismos labels; lo único que cambia por contexto son el binding de API, los ids/scopes
 * y el prefijo de permisos, que se inyectan por props.
 *
 * Orden: Ver detalle · Gestionar roles · Editar · Reenviar invitación (INVITED) · Restablecer contraseña
 * (ACTIVE) · Restablecer MFA (ACTIVE) · Deshabilitar/Habilitar · Eliminar. Un sistema (API) cambia
 * invitación, contraseña y MFA por Rotar secreto y Editar IPs, y su Editar solo toca nombre y contacto.
 *
 * Cada acción de escritura está gateada por su permiso (el backend re-enforza) y condicionada por el
 * estado del usuario. Las destructivas piden confirmación. `modal={false}` evita el page-lock de Radix
 * (pointer-events:none en body) cuando la fila se desmonta tras confirmar una acción.
 */
export function UserActionsMenu({
    user,
    tenantId,
    gateScope,
    gateScopeRef,
    api,
    perms,
    detailQueryKey,
    listQueryKey,
    labels,
}: Props) {
    const qc = useQueryClient();
    const { hasPermission, hasScopeAccess } = useAuth();
    const sub = user.cognitoSub;
    const status = user.status;
    const [confirm, setConfirm] = useState<null | 'disable' | 'delete' | 'rotate'>(null);
    const [detailOpen, setDetailOpen] = useState(false);
    const [manageRolesOpen, setManageRolesOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [tempPass, setTempPass] = useState<string | null>(null);
    const isApiUser = user.accountType === 'API';
    // Un sistema no tiene correo real: los textos lo nombran por su nombre.
    const userLabel = isApiUser ? user.displayName : user.email;
    // Secreto rotado: solo en este estado local, nunca en la caché de queries ni de mutaciones.
    const [apiCredentials, setApiCredentials] = useState<ApiUserCredentialsLocal | null>(null);
    const [apiAccessOpen, setApiAccessOpen] = useState(false);

    // Detalle enriquecido (roleAssignments con scope/scopeRef + nombre legible del destino). Solo al abrir.
    const {
        data: detail,
        isError: detailError,
        refetch: refetchDetail,
    } = useQuery({
        queryKey: detailQueryKey,
        queryFn: api.getDetail,
        enabled: detailOpen,
    });

    // Catálogo de campos personalizados (enabled) del tenant, para mostrarlos en "Ver detalle".
    const { data: customFields = [] } = useTenantUserFields(tenantId, { enabledOnly: true });
    // Orden de presentación: alfabético por label (default del proyecto).
    const sortedCustomFields = [...customFields].sort((a, b) => a.label.localeCompare(b.label));

    const invalidate = (): Promise<void> => qc.invalidateQueries({ queryKey: listQueryKey });

    const disableMut = useMutation({
        mutationFn: () => api.disable(),
        onSuccess: async () => {
            toast.success('Usuario deshabilitado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const enableMut = useMutation({
        mutationFn: () => api.enable(),
        onSuccess: async () => {
            toast.success('Usuario habilitado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const deleteMut = useMutation({
        mutationFn: () => api.delete(),
        onSuccess: async () => {
            toast.success('Usuario eliminado');
            await invalidate();
        },
        onError: handleApiError,
    });
    const resetMfaMut = useMutation({
        mutationFn: () => api.resetMfa(),
        onSuccess: () => {
            toast.success('MFA restablecido');
        },
        onError: handleApiError,
    });
    const resendMut = useMutation({
        mutationFn: () => api.resendInvitation(),
        onSuccess: () => {
            toast.success('Invitación reenviada');
        },
        onError: handleApiError,
    });
    const resetPwMut = useMutation({
        mutationFn: () => api.resetPassword(),
        onSuccess: (r) => {
            setTempPass(r.temporaryPassword);
        },
        onError: handleApiError,
    });
    const rotateMut = useMutation({
        mutationFn: async (): Promise<void> => {
            setApiCredentials(await api.rotateApiSecret());
        },
        onError: handleApiError,
    });
    const roleNames = detail?.roles ?? user.roles ?? [];
    const ipAllowlist = detail?.ipAllowlist ?? user.ipAllowlist ?? [];
    const technicalContact = detail?.technicalContact ?? user.technicalContact;

    // Sin scope o sin ninguna acción habilitada no mostramos el "⋮" (cada item igual se gatea con
    // <Gate>; esto solo evita un menú vacío). Va DESPUÉS de todos los hooks.
    const actionPerms: Permission[] = [
        'tenant.role.assign',
        perms.update,
        perms.passwordReset,
        perms.mfaReset,
        perms.status,
        perms.delete,
        API_USER_MANAGE_PERMISSION,
    ];
    if (!hasScopeAccess(gateScope, gateScopeRef) || !actionPerms.some((p) => hasPermission(p))) {
        return null;
    }

    return (
        <>
            <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" aria-label="Acciones">
                        <MoreVertical className="size-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem
                        onClick={() => {
                            setDetailOpen(true);
                        }}
                    >
                        Ver detalle
                    </DropdownMenuItem>
                    <Gate permission="tenant.role.assign" scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            onClick={() => {
                                setManageRolesOpen(true);
                            }}
                        >
                            Gestionar roles
                        </DropdownMenuItem>
                    </Gate>
                    <Gate permission={perms.update} scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            onClick={() => {
                                setEditOpen(true);
                            }}
                        >
                            Editar
                        </DropdownMenuItem>
                    </Gate>
                    {!isApiUser && status === 'INVITED' && (
                        <Gate
                            permission={perms.passwordReset}
                            scope={gateScope}
                            scopeRef={gateScopeRef}
                        >
                            <DropdownMenuItem
                                onClick={() => {
                                    resendMut.mutate();
                                }}
                            >
                                Reenviar invitación
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.activationLink} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {!isApiUser && status === 'ACTIVE' && (
                        <Gate
                            permission={perms.passwordReset}
                            scope={gateScope}
                            scopeRef={gateScopeRef}
                        >
                            <DropdownMenuItem
                                onClick={() => {
                                    resetPwMut.mutate();
                                }}
                            >
                                Restablecer contraseña
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.passwordReset} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {!isApiUser && status === 'ACTIVE' && (
                        <Gate permission={perms.mfaReset} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    resetMfaMut.mutate();
                                }}
                            >
                                Restablecer MFA
                                {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- wrapper solo frena la propagación al DropdownMenuItem; el control interactivo real es el botón del HelpHint */}
                                <span
                                    className="ml-auto"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    <HelpHint text={HELP_TEXT.mfaReset} side="right" />
                                </span>
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {isApiUser && (
                        <Gate
                            permission={API_USER_MANAGE_PERMISSION}
                            scope={gateScope}
                            scopeRef={gateScopeRef}
                        >
                            <DropdownMenuItem
                                onClick={() => {
                                    setConfirm('rotate');
                                }}
                            >
                                Rotar secreto
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    setApiAccessOpen(true);
                                }}
                            >
                                Editar IPs
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    <DropdownMenuSeparator />
                    {status === 'ACTIVE' && (
                        <Gate permission={perms.status} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    setConfirm('disable');
                                }}
                            >
                                Deshabilitar
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    {status === 'SUSPENDED' && (
                        <Gate permission={perms.status} scope={gateScope} scopeRef={gateScopeRef}>
                            <DropdownMenuItem
                                onClick={() => {
                                    enableMut.mutate();
                                }}
                            >
                                Habilitar
                            </DropdownMenuItem>
                        </Gate>
                    )}
                    <Gate permission={perms.delete} scope={gateScope} scopeRef={gateScopeRef}>
                        <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => {
                                setConfirm('delete');
                            }}
                        >
                            Eliminar
                        </DropdownMenuItem>
                    </Gate>
                </DropdownMenuContent>
            </DropdownMenu>

            <ManageUserRolesDialog
                open={manageRolesOpen}
                onOpenChange={setManageRolesOpen}
                tenantId={tenantId}
                cognitoSub={sub}
                userLabel={userLabel}
                fetchDetail={api.getDetail}
                detailQueryKey={detailQueryKey}
                listQueryKey={listQueryKey}
                gateScope={gateScope}
                gateScopeRef={gateScopeRef}
                labels={labels}
                accountType={user.accountType}
            />

            <ConfirmDialog
                open={confirm === 'disable'}
                onOpenChange={(o) => {
                    if (!o) setConfirm(null);
                }}
                title="Deshabilitar usuario"
                description={`${userLabel} no podrá iniciar sesión hasta que lo habilites de nuevo. ¿Continuar?`}
                confirmLabel="Deshabilitar"
                destructive
                onConfirm={() => {
                    disableMut.mutate();
                }}
            />
            <ConfirmDialog
                open={confirm === 'delete'}
                onOpenChange={(o) => {
                    if (!o) setConfirm(null);
                }}
                title="Eliminar usuario"
                description={`Se eliminará a ${userLabel}. Esta acción no se puede deshacer. ¿Continuar?`}
                confirmLabel="Eliminar"
                destructive
                loading={deleteMut.isPending}
                onConfirm={() => {
                    deleteMut.mutate();
                }}
            />
            <ConfirmDialog
                open={confirm === 'rotate'}
                onOpenChange={(o) => {
                    if (!o) setConfirm(null);
                }}
                title="Rotar secreto"
                description={`Se generará un secreto nuevo para ${userLabel}. El actual y los tokens vigentes dejarán de servir en menos de un minuto. ¿Continuar?`}
                confirmLabel="Rotar secreto"
                destructive
                loading={rotateMut.isPending}
                onConfirm={() => {
                    rotateMut.mutate();
                }}
            />
            {isApiUser && (
                <EditApiAccessDialog
                    save={api.updateApiAccess}
                    systemName={userLabel}
                    open={apiAccessOpen}
                    onOpenChange={setApiAccessOpen}
                    initialIpAllowlist={ipAllowlist}
                    onSaved={() => {
                        void invalidate();
                        void qc.invalidateQueries({ queryKey: detailQueryKey });
                    }}
                />
            )}
            <ApiUserCredentialsModal
                credentials={apiCredentials}
                tenantId={tenantId}
                systemName={userLabel}
                onConfirm={() => {
                    setApiCredentials(null);
                }}
            />

            {/* Ver detalle: datos del usuario + roles y alcance (resueltos por el GET single). */}
            <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Detalle del usuario</DialogTitle>
                        <DialogDescription>{userLabel}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-1 text-sm">
                            <div>
                                <span className="text-muted-foreground">Nombre: </span>
                                {user.displayName}
                            </div>
                            {isApiUser ? (
                                <>
                                    <div>
                                        <span className="text-muted-foreground">Tipo: </span>
                                        Sistema (API)
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className="text-muted-foreground">Client ID:</span>
                                        <CopyableId label="Client ID" value={sub} />
                                    </div>
                                    <div>
                                        <span className="text-muted-foreground">
                                            Contacto técnico:{' '}
                                        </span>
                                        {technicalContact ?? '—'}
                                    </div>
                                    <div className="space-y-1">
                                        <span className="text-muted-foreground">
                                            IPs permitidas:
                                        </span>
                                        {ipAllowlist.length > 0 ? (
                                            <ul className="flex flex-wrap gap-1">
                                                {[...ipAllowlist].sort().map((ip) => (
                                                    <li
                                                        key={ip}
                                                        className="rounded bg-muted px-2 py-0.5 font-mono text-xs"
                                                    >
                                                        {ip}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span> —</span>
                                        )}
                                    </div>
                                </>
                            ) : (
                                <div>
                                    <span className="text-muted-foreground">Email: </span>
                                    {user.email}
                                </div>
                            )}
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">Estado:</span>
                                <StatusBadge status={status} />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <p className="flex items-center gap-1 text-sm font-medium">
                                Roles y alcance
                                <HelpHint text={HELP_TEXT.roleAssignment} />
                            </p>
                            {detailError ? (
                                <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm text-destructive">
                                        No pudimos cargar el detalle del usuario.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => {
                                            void refetchDetail();
                                        }}
                                    >
                                        Reintentar
                                    </Button>
                                </div>
                            ) : (detail?.roleAssignments ?? []).length > 0 ? (
                                <ul className="space-y-1">
                                    {detail?.roleAssignments?.map((a) => (
                                        <li
                                            key={`${a.roleId}-${a.scope}-${a.scopeRef}`}
                                            className="flex flex-wrap items-center gap-2 text-sm"
                                        >
                                            <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                                {assignmentRoleName(a)}
                                            </span>
                                            <span className="text-muted-foreground">
                                                {assignmentScopeLabel(a, labels)}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            ) : roleNames.length === 0 ? (
                                <p className="text-sm text-muted-foreground">Sin roles</p>
                            ) : (
                                <ul className="flex flex-wrap gap-1">
                                    {roleNames.map((r) => (
                                        <li
                                            key={r}
                                            className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground"
                                        >
                                            {r}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>

                        {/* Campos personalizados del tenant (solo lectura). Muestra todos los enabled
                            con su valor o "—" si vacío. Orden alfabético por label. */}
                        {sortedCustomFields.length > 0 && (
                            <div className="space-y-1">
                                <p className="text-sm font-medium">Campos personalizados</p>
                                <dl className="space-y-1 text-sm">
                                    {sortedCustomFields.map((f) => (
                                        <div key={f.key} className="flex gap-2">
                                            <dt className="text-muted-foreground">{f.label}:</dt>
                                            <dd>{detail?.customFields?.[f.key] || '—'}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDetailOpen(false);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Persona: nombre, teléfono y campos personalizados. Sistema: nombre y contacto técnico. */}
            {isApiUser ? (
                <EditApiUserDialog
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    systemName={user.displayName}
                    technicalContact={technicalContact}
                    canEditContact={
                        hasPermission(API_USER_MANAGE_PERMISSION) &&
                        hasScopeAccess(gateScope, gateScopeRef)
                    }
                    update={api.update}
                    updateApiAccess={api.updateApiAccess}
                    onSaved={async () => {
                        await Promise.all([
                            invalidate(),
                            qc.invalidateQueries({ queryKey: detailQueryKey }),
                        ]);
                    }}
                />
            ) : (
                <EditUserDialog
                    open={editOpen}
                    onOpenChange={setEditOpen}
                    userEmail={userLabel}
                    userDisplayName={user.displayName}
                    tenantId={tenantId}
                    getDetail={api.getDetail}
                    detailQueryKey={detailQueryKey}
                    update={api.update}
                    onSaved={invalidate}
                />
            )}

            {/* Contraseña temporal tras restablecer */}
            <Dialog
                open={tempPass !== null}
                onOpenChange={(o) => {
                    if (!o) setTempPass(null);
                }}
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Contraseña temporal</DialogTitle>
                        <DialogDescription>
                            Compártela con {user.email}. Deberá cambiarla al iniciar sesión.
                        </DialogDescription>
                    </DialogHeader>
                    <code className="block select-all rounded bg-muted p-3 text-center font-mono text-lg">
                        {tempPass}
                    </code>
                    <DialogFooter>
                        <Button
                            onClick={() => {
                                setTempPass(null);
                            }}
                        >
                            Cerrar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
